import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Building2, Plus, Settings, Users, Copy, CheckCircle2, MessageSquare, Cog } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import ImageUploader from '../components/ImageUploader';
import MobileNav from '../components/MobileNav';
import Logo from '../components/Logo';

const Properties = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    nom: '',
    adresse: '',
    description: '',
    image_url: '',
    actif: true,
  });

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) setUser(JSON.parse(userStr));
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await api.get('/properties');
      setProperties(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des logements');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/properties', formData);
      toast.success('Logement ajouté avec succès !');
      setDialogOpen(false);
      setFormData({ nom: '', adresse: '', description: '', image_url: '', actif: true });
      fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
    }
  };

  const toggleActif = async (propertyId, currentStatus) => {
    try {
      await api.put(`/properties/${propertyId}`, { actif: !currentStatus });
      toast.success('Statut mis à jour');
      fetchProperties();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const copyLink = (link) => {
    const fullLink = `${window.location.origin}/p/${link}`;
    navigator.clipboard.writeText(fullLink);
    toast.success('Lien copié dans le presse-papiers !');
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/auth');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/50 flex items-center justify-center">
        <div className="text-slate-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50">
      {/* Desktop Sidebar - Hidden on mobile */}
      <aside className="hidden lg:block fixed left-0 top-0 h-full w-64 bg-white border-r border-slate-100 p-6 space-y-8">
        <div className="flex items-center cursor-pointer" onClick={() => navigate('/dashboard')}>
          <Logo variant="full" size="h-12" />
        </div>

        <nav className="space-y-2">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="w-full justify-start text-slate-600 hover:bg-slate-100 hover:text-slate-900">
            Dashboard
          </Button>
          <Button variant="ghost" className="w-full justify-start text-rose-600 bg-rose-50 hover:bg-rose-100">
            <Building2 className="w-5 h-5 mr-3" strokeWidth={1.5} />
            Logements
          </Button>
          <Button variant="ghost" onClick={() => navigate('/revenue')} className="w-full justify-start text-slate-600 hover:bg-slate-100 hover:text-slate-900">
            Revenus
          </Button>
        </nav>
      </aside>

      <div className="lg:ml-64">
        {/* Header - Mobile optimized */}
        <header className="bg-white border-b border-slate-100 px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            {/* Mobile menu */}
            <MobileNav user={user} onLogout={handleLogout} />
            
            {/* Mobile logo */}
            <div className="flex items-center lg:hidden">
              <Logo variant="full" size="h-10" />
            </div>
            
            {/* Desktop title */}
            <div className="hidden lg:block">
              <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Mes Logements</h1>
              <p className="text-sm sm:text-base text-slate-600 mt-1">Gérez vos logements et leurs options</p>
            </div>
            
            {/* Add button */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-property-btn" className="bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-sm sm:text-base">
                  <Plus className="w-4 h-4 sm:mr-2" strokeWidth={1.5} />
                  <span className="hidden sm:inline">Ajouter</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto mx-4">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Nouveau Logement</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="nom">Nom du logement</Label>
                    <Input
                      id="nom"
                      value={formData.nom}
                      onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                      required
                      data-testid="property-nom-input"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="adresse">Adresse</Label>
                    <Input
                      id="adresse"
                      value={formData.adresse}
                      onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                      required
                      data-testid="property-adresse-input"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                      data-testid="property-description-input"
                      className="mt-1"
                      rows={3}
                    />
                  </div>
                  <div>
                    <ImageUploader
                      value={formData.image_url}
                      onChange={(url) => setFormData({ ...formData, image_url: url })}
                      label="Image du logement (recommandée)"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="actif"
                      checked={formData.actif}
                      onCheckedChange={(checked) => setFormData({ ...formData, actif: checked })}
                      data-testid="property-actif-switch"
                    />
                    <Label htmlFor="actif">Logement actif</Label>
                  </div>
                  <Button type="submit" data-testid="property-submit-btn" className="w-full bg-rose-600 hover:bg-rose-700 text-white rounded-lg">
                    Créer le logement
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          
          {/* Mobile title */}
          <div className="lg:hidden mt-4">
            <h1 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Mes Logements</h1>
            <p className="text-sm text-slate-600 mt-1">Gérez vos logements</p>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8">
          {properties.length === 0 ? (
            <Card className="bg-white border border-slate-100 shadow-sm rounded-xl p-8 sm:p-12 text-center" data-testid="empty-properties-state">
              <Building2 className="w-12 sm:w-16 h-12 sm:h-16 text-slate-300 mx-auto mb-3 sm:mb-4" strokeWidth={1.5} />
              <h3 className="text-lg sm:text-xl font-medium text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Aucun logement</h3>
              <p className="text-sm text-slate-500 mb-4 sm:mb-6">Commencez par ajouter votre premier logement</p>
              <Button onClick={() => setDialogOpen(true)} className="bg-rose-600 hover:bg-rose-700 text-white rounded-lg">
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Ajouter un logement
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="properties-grid">
              {properties.map((property) => (
                <Card key={property.id} className="bg-white border border-slate-100 shadow-sm rounded-xl overflow-hidden hover-lift" data-testid={`property-card-${property.id}`}>
                  {/* Image du logement */}
                  <div className="relative h-40 sm:h-48 bg-slate-100">
                    {property.image_url ? (
                      <img 
                        src={property.image_url} 
                        alt={property.nom}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className={`absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 ${property.image_url ? 'hidden' : ''}`}
                      style={{ display: property.image_url ? 'none' : 'flex' }}
                    >
                      <Building2 className="w-12 h-12 text-slate-300" strokeWidth={1} />
                    </div>
                    {/* Badge actif/inactif */}
                    <div className="absolute top-3 right-3">
                      <Switch
                        checked={property.actif}
                        onCheckedChange={() => toggleActif(property.id, property.actif)}
                        data-testid={`property-toggle-${property.id}`}
                      />
                    </div>
                  </div>
                  
                  <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg sm:text-xl font-medium text-slate-900 mb-1 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{property.nom}</h3>
                      <p className="text-xs sm:text-sm text-slate-500 truncate">{property.adresse}</p>
                    </div>

                    <p className="text-xs sm:text-sm text-slate-600 line-clamp-2">{property.description}</p>

                    <div className="flex items-center space-x-2 p-2 bg-slate-50 rounded-lg">
                      <code className="text-xs text-slate-600 flex-1 truncate">.../{property.lien_unique}</code>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyLink(property.lien_unique)}
                        data-testid={`copy-link-btn-${property.id}`}
                        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 flex-shrink-0"
                      >
                        <Copy className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        size="sm"
                        onClick={() => navigate(`/properties/${property.id}/options`)}
                        data-testid={`manage-options-btn-${property.id}`}
                        className="flex-1 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs sm:text-sm"
                      >
                        <Settings className="w-4 h-4 mr-1 sm:mr-2" strokeWidth={1.5} />
                        Options
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => navigate(`/properties/${property.id}/messages`)}
                        data-testid={`manage-messages-btn-${property.id}`}
                        className="flex-1 bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100 rounded-lg text-xs sm:text-sm"
                      >
                        <MessageSquare className="w-4 h-4 mr-1 sm:mr-2" strokeWidth={1.5} />
                        Messages
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => navigate(`/properties/${property.id}/providers`)}
                        data-testid={`manage-providers-btn-${property.id}`}
                        className="flex-1 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg text-xs sm:text-sm"
                      >
                        <Users className="w-4 h-4 mr-1 sm:mr-2" strokeWidth={1.5} />
                        Prestataires
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => navigate(`/properties/${property.id}/settings`)}
                        data-testid={`manage-settings-btn-${property.id}`}
                        className="flex-1 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg text-xs sm:text-sm"
                      >
                        <Cog className="w-4 h-4 mr-1 sm:mr-2" strokeWidth={1.5} />
                        Paramètres
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Properties;
